import { Select } from '@domgen/base-design-system-2.0/atoms/Select';
import { useEffect, useRef, useState } from 'react';
import { gaEvent } from '@domgen/shared/services/ga/ga';
import { getSystemCode } from '../../utils/getSystemCode';
import { useUrlParams } from '../../store/selectors/useUrlParams';
import { useAppState } from '../../store/selectors/useAppState';
import { formApplianceName } from '../../utils/store/formApplianceName';
import { formCategoryName } from '../../utils/store/formCategoryName';

export type ModelNumberLocationAppliance = {
  locations?: string[];
  imageSrc?: string;
  images?: { key: string; symbol: string }[];
};

export type ModelNumberLocationCategory = {
  leadText: string;
  appliances?: ModelNumberLocationAppliance[];
};

export type ModelNumberLocationContent = Record<string, ModelNumberLocationCategory>;

type ModelNumberLocationProps = {
  isOpen: boolean;
  handleClose: (e: boolean) => boolean | void;
  options: { label: string; value: string }[];
  content: Record<string, ModelNumberLocationCategory>;
  pagename?: string;
};

const styles = {
  modelNumberLocationContainer:
    'px-space-600 pb-space-800 flex flex-col shrink-0 h-full max-h-[calc(100svh-4rem)] max-h-[75vh] tablet:max-h-screen overflow-y-auto overscroll-contain mobile:min-h-120',
  modelNumberLocationLayoutContainer:
    'sticky top-0 z-20 bg-bg-main-container inline-flex justify-between gap-space-100 items-start w-full pt-space-600 pb-space-200 mb-space-400 shrink-0',
  sideDrawFindModelNumberPhoneNumberContainer:
    'flex flex-col gap-space-600 pt-space-600 pb-space-800 border-t-2 border-(--color-neutral-200) shrink-0',
  sideDrawFindModelNumberPhoneNumber:
    'border-2 border-(--color-neutral-200) rounded-radius-200 py-space-400 px-space-500',
  contentContainer:
    'flex flex-col items-center tablet:items-start desktop:items-start w-full gap-space-400 mb-space-600 pointer-events-auto',
};

const Close = (
  <svg width="24" height="32" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M18.375 19.8125L10.125 11.5L1.8125 19.8125C1.4375 20.1875 0.8125 20.1875 0.375 19.8125C0 19.375 0 18.75 0.375 18.375L8.6875 10.0625L0.4375 1.8125C0 1.4375 0 0.8125 0.4375 0.375C0.8125 0 1.4375 0 1.8125 0.375L10.125 8.6875L18.375 0.375C18.75 0 19.375 0 19.8125 0.375C20.1875 0.8125 20.1875 1.4375 19.8125 1.8125L11.5 10.0625L19.8125 18.375C20.1875 18.75 20.1875 19.375 19.8125 19.8125C19.375 20.1875 18.75 20.1875 18.375 19.8125Z"
      fill="currentColor"
    />
  </svg>
);

export const ModelNumberLocation = ({
  isOpen,
  handleClose,
  options,
  content,
  pagename = 'olr-landing',
}: ModelNumberLocationProps) => {
  const [selectedOption, setOption] = useState('');
  const [displayContent, setDisplayContent] = useState<ModelNumberLocationCategory>(
    {} as ModelNumberLocationCategory,
  );
  const phoneNumber = '+1 833-552-2314';
  const [contentVisible, setContentVisible] = useState(false);
  const contentTransition = useRef<number | null>(null);
  const urlParams = useUrlParams();
  const systemCode = getSystemCode(urlParams?.rs);
  const { session } = useAppState();
  const applianceName = formApplianceName.get();
  const applianceCategory = formCategoryName.get();

  useEffect(() => {
    if (!isOpen) {
      setOption('');
      setDisplayContent({} as ModelNumberLocationCategory);
      setContentVisible(false);
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (contentTransition.current !== null) {
        window.clearTimeout(contentTransition.current);
      }
    };
  }, []);

  const onSelectOptionChange = (option: string) => {
    const selectedLabel = options.find((item) => item.value === option)?.label ?? option;

    if (contentTransition.current !== null) {
      window.clearTimeout(contentTransition.current);
    }

    gaEvent({
      eventCategory: 'appliance details form USA',
      eventAction: 'appliance',
      eventLabel: selectedLabel,
      pagename,
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      applianceName,
      appSessionID: session?.id,
    });

    setContentVisible(false);

    contentTransition.current = window.setTimeout(() => {
      setOption(option);

      setDisplayContent(content[option]);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setContentVisible(true);
        });
      });
    }, 200);
  };

  return (
    <div className={styles.modelNumberLocationContainer}>
      <div className={styles.modelNumberLocationLayoutContainer}>
        <h2 className="text-h3 mb-space-200 text-text-headline">How to find your model number</h2>
        <button
          type="button"
          onClick={() => handleClose(false)}
          aria-label="Close side drawer"
          tabIndex={isOpen ? 0 : -1}
          className="cursor-pointer text-text-link-default p-2 -mr-2 -mt-2 min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation z-20"
        >
          {Close}
        </button>
      </div>
      <p className="text-body-large mb-space-200">
        Choose your appliance to see where to find your rating plate.
      </p>
      <Select
        options={options}
        placeholder="Select an appliance"
        value={selectedOption}
        onChange={onSelectOptionChange}
        maxHeight={300}
        ariaLabel="Select an appliance"
      />

      {content && (
        <div
          className={`transition-all duration-400 ease-in-out ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 tablet:-translate-y-4'}`}
        >
          <article className={styles.contentContainer}>
            {displayContent.leadText && (
              <p className="text-body-large text-left mt-space-600 mb-0 w-full">
                {displayContent.leadText}
              </p>
            )}

            {displayContent.appliances?.map(
              (appliance: ModelNumberLocationAppliance, idx: number) => (
                <div key={idx} className="w-full">
                  {appliance.locations && appliance.locations.length > 0 && (
                    <ol className="list-decimal list-outside ml-space-600 space-y-space-100 text-body-large text-left my-space-200">
                      {appliance.locations.map((line: string, idx: number) => (
                        <li key={idx} className="pl-space-100">
                          {line}
                        </li>
                      ))}
                    </ol>
                  )}
                  <div className="flex flex-col w-full items-center justify-center gap-space-300 min-h-60">
                    {appliance.imageSrc ? (
                      <img
                        key={appliance.imageSrc}
                        src={appliance.imageSrc}
                        alt={appliance.locations?.join(', ') || 'Appliance model number location'}
                        className="max-h-60 w-auto object-contain my-space-300"
                        loading="eager"
                        decoding="async"
                      />
                    ) : null}
                  </div>
                </div>
              ),
            )}
          </article>
        </div>
      )}

      <article className={styles.sideDrawFindModelNumberPhoneNumberContainer}>
        <div className={styles.sideDrawFindModelNumberPhoneNumber}>
          <p className="text-body-strong mb-space-200">Still can’t find it?</p>
          <p className="text-body">
            If your model or serial numbers aren’t recognized or you don’t have them to hand, call
            us at{' '}
            <a
              href={`tel:${phoneNumber.replace(/[^\d+]/g, '')}`}
              className="text-body-link-strong text-text-body"
            >
              {phoneNumber}
            </a>{' '}
            for support.
          </p>
        </div>
      </article>
    </div>
  );
};
